import { action, computed, observable } from 'mobx'
import WebPageModel from './WebPageModel'
import ListStore from '../../../shared-lib/stores/ListStore'

export default class WebPageStore extends ListStore<WebPageModel> {
  @observable accessor loading = false
  fetchMethodName = 'webPages'
  ModelClass = WebPageModel

  @computed get currentWebPage() {
    if (!this.editItem) this.createEditItem('')
    return this.editItem as WebPageModel
  }

  @action saveCurrentWebPage = async (): Promise<boolean> => {
    this.loading = true

    try {
      const result = await this.editItem?.updateWebPage()
      this.findItemById(result?.data._id)?.merge(result?.data)
      return !!result
    } catch (error) {
      this.rootStore.errorHandler.captureException(error, {
        context: 'saveCurrentWebPage',
        webPageId: this.editItem?.data._id,
        webPagePath: this.editItem?.data.path,
      })
      return false
    } finally {
      this.loading = false
    }
  }

  @action deleteWebPage = async (path: string): Promise<boolean> => {
    try {
      const response = await this.rootStore.communication.requester.deleteWebPage({ path })
      this.items = this.items.filter(item => item.data.path !== path)
      return !!response.deleteWebPage
    } catch (error) {
      return false
    }
  }

  @action reorderPages = async (sortedPageIds: string[]) => {
    const reorderedPages = sortedPageIds
      .map(id => this.items.find(item => item.data._id === id))
      .filter(Boolean)

    reorderedPages.forEach((page, index) => page!.data.order = index)

    await Promise.all(reorderedPages.map(page => page!.update_api()))
  }

  @action createDefaultPages = async () => {
    this.loading = true
    try {
      const response = await this.rootStore.communication.requester.createDefaultWebPages()
      this.mergeData(response.createDefaultWebPages)
      return true
    } catch {
      return false
    } finally {
      this.loading = false
    }
  }

  @action dev_RemoveAllWebPages = async () => {
    this.loading = true
    const response = await this.rootStore.communication.requester.dev_RemoveAllWebPages()
    this.loading = false

    if (response.dev_RemoveAllWebPages) {
      this.clearData()
    }
  }

  @action clearCurrentWebPage = () => {
    this.createEditItem('')
  }

  webPageByPath = (path: string) => {
    return this.items.find(item => item.data.path === path)
  }

  @action invalidatePages = async () => {
    this.rootStore.communication.requester.invalidatePages()
  }

  @action findPageReferences = async (path: string) => {
    const response = await this.rootStore.communication.requester.findPageReferences({ path, professionalId: this.rootStore.userStore.data._id })
    return response.findPageReferences.totalCount
  }

  @action toggleWebsitePublic = async (isPublic: boolean) => {
    const {
      userStore: {
        data,
        updateSaasSettings_api,
      },
      i18n: { s },
      dropdownMessageHandler,
    } = this.rootStore

    const success = await updateSaasSettings_api({ isPublic })

    if (success) {
      data.professionalProfile.saasSettings.isPublic = isPublic
      this.invalidatePages()
      dropdownMessageHandler.showDropdownMessage({
        type: 'success',
        title: s('Success'),
        message: s('Your changes have been saved.'),
      })
    } else {
      dropdownMessageHandler.showDropdownMessage({
        type: 'error',
        title: s('Error'),
        message: s('Unable to update booking website settings.'),
      })
    }
  }
}
